// Min side — hvem er jeg, hvilke tokens har jeg, og hva vet tjenesten om meg.
//
// ANSATTNUMMERET ER DET MEST SENSITIVE FELTET I MODULEN, og denne skjermen er
// det eneste stedet en innlogget bruker kan røre det. Reglene som er bygget inn:
//  · Vi VISER bare den maskerte formen tjenesten sender («•••321»). Klienten har
//    ikke noe felt som kunne båret klartekst — heller ikke skjult.
//  · Inndatafeltet for å SETTE et nytt nummer er alltid tomt. Å forhåndsfylle
//    det med den nåværende verdien ville krevd klartekst fra tjenesten, og da
//    hadde maskeringen vært teater.
//  · Feltet er `password`, så en skjermdeling ikke fanger sifrene mens de tastes.
import type { Kontekst } from './kontekst.js';
import type { TokenRad } from '../api/typer.js';
import { formaterDatoTid } from '../lib/tid.js';
import { div, felt, h, knapp, kopierknapp, merke, nv, p, sett, span } from '../ui/dom.js';
import { ikon } from '../ui/ikoner.js';
import { registreringsLenke } from '../lib/rute.js';

export interface MegValg {
  /** Logg ut — sletter tokenet fra begge nettleserlagre og går til innlogging. */
  loggUt(): void;
}

export function visMeg(rot: HTMLElement, ctx: Kontekst, valg: MegValg): void {
  const side = div('side');
  sett(rot, side);
  tegn();

  function tegn(): void {
    sett(side);
    const topp = div('side-topp');
    const titler = div('');
    titler.append(h(1, 'side-tittel', 'Min side'));
    titler.append(p('side-under', 'Pseudonymet ditt, rettigheter og tokens.'));
    topp.append(titler);
    const knapper = div('filterrad');
    knapper.append(knapp('Logg ut', () => valg.loggUt(), { variant: 'fare', ikon: ikon('ut') }));
    topp.append(knapper);
    side.append(topp);

    side.append(personkort());
    side.append(ansattnummerkort());
    side.append(tokenkort());
    side.append(lenkekort());
  }

  function personkort(): HTMLElement {
    const per = ctx.meg.person;
    const kort = div('kort');
    const hode = div('kort-hode');
    hode.append(ikon('person'), h(2, 'kort-tittel', 'Deg'));
    if (per.kanAttestere) hode.append(merke('Kan attestere', 'gull'));
    kort.append(hode);

    kort.append(nv('Pseudonym', per.id));
    kort.append(nv('Navn lagret hos tjenesten', per.navn ?? 'nei — bare pseudonymet'));
    kort.append(nv('Tilhørighet', per.tilhorighet ?? 'ikke satt'));
    kort.append(nv('Registrert', formaterDatoTid(per.opprettetTsMs)));
    kort.append(p('kort-brød', 'Andre ser bare pseudonymet ditt og om du kan attestere. '
      + 'Navn, tilhørighet og ansattnummer følger aldri med i sammenligningsvisninger.'));
    return kort;
  }

  function ansattnummerkort(): HTMLElement {
    const per = ctx.meg.person;
    const kort = div('kort');
    const hode = div('kort-hode');
    hode.append(ikon('info'), h(2, 'kort-tittel', 'Ansattnummer'));
    kort.append(hode);

    kort.append(nv('Registrert hos tjenesten', per.ansattnummerSatt ? per.ansattnummer : 'ikke satt'));
    kort.append(p('kort-brød', 'Dette er koden Heimevernet fører timer på. Tjenesten viser den '
      + 'bare maskert — også til deg. Skriv inn hele nummeret for å endre det.'));

    const skjema = document.createElement('form');
    skjema.className = 'skjema-rad';
    skjema.noValidate = true;
    const nytt = felt('Nytt ansattnummer', {
      type: 'password', plassholder: '4–10 siffer', inndatamodus: 'numeric', autofullfør: 'off',
    });
    const lagre = knapp('Lagre', undefined, { variant: 'primær', type: 'submit' });
    skjema.append(nytt.rot, lagre);
    kort.append(skjema);

    if (per.ansattnummerSatt) {
      kort.append(knapp('Fjern ansattnummeret', () => void sett_(null), { variant: 'stille' }));
    }

    skjema.addEventListener('submit', (e) => {
      e.preventDefault();
      const v = nytt.input.value.trim();
      if (!/^\d{4,10}$/.test(v)) {
        nytt.settFeil('Ansattnummer skal være 4–10 siffer.');
        return nytt.input.focus();
      }
      nytt.settFeil(null);
      void sett_(v);
    });

    async function sett_(verdi: string | null): Promise<void> {
      lagre.disabled = true;
      const r = await ctx.klient.settAnsattnummer(verdi);
      lagre.disabled = false;
      if (!r.ok) {
        nytt.settFeil(r.feil.melding);
        return;
      }
      // Tømmes med en gang: sifrene skal ikke bli stående i skjemaet etterpå.
      nytt.input.value = '';
      ctx.kvitter(verdi ? 'Ansattnummeret er lagret' : 'Ansattnummeret er fjernet');
      await ctx.oppdaterMeg();
      tegn();
    }

    return kort;
  }

  function tokenkort(): HTMLElement {
    const kort = div('kort');
    const hode = div('kort-hode');
    hode.append(ikon('lenke'), h(2, 'kort-tittel', 'Tokens'));
    hode.append(span('kort-hode-under', `${ctx.meg.tokens.length} utstedt`));
    kort.append(hode);
    kort.append(p('kort-brød', 'Bare id-delen vises — hemmeligheten er lagret hashet og '
      + 'finnes ingen steder i klartekst. Nye tokens utstedes av drift.'));
    for (const t of ctx.meg.tokens) kort.append(tokenrad(t));
    return kort;
  }

  function tokenrad(t: TokenRad): HTMLElement {
    const rad = div(`tokenrad${t.tilbaketrukketTsMs ? ' trukket' : ''}`);
    const venstre = div('tokenrad-tekst');
    const kode = document.createElement('code');
    kode.className = 'kodeverdi';
    kode.textContent = t.maskert;
    venstre.append(kode);
    venstre.append(span('tokenrad-navn', t.navn || 'uten navn'));
    rad.append(venstre);

    const merker = div('tokenrad-merker');
    if (t.aktivtNa) merker.append(merke('Brukes nå', 'gull'));
    if (t.tilbaketrukketTsMs) merker.append(merke('Tilbaketrukket', 'fare'));
    merker.append(merke(`Utstedt ${formaterDatoTid(t.opprettetTsMs)}`));
    merker.append(merke(t.sistBruktTsMs ? `Sist brukt ${formaterDatoTid(t.sistBruktTsMs)}` : 'Aldri brukt'));
    rad.append(merker);
    return rad;
  }

  /**
   * Bygg en delbar registreringslenke av en LØS KODE noen har fått utlevert.
   *
   * Dette er ikke stedet man lager lenker: har du attestasjonsrett, gjør du det
   * på «Inviter»-skjermen, som også lister og tilbakekaller dem. Boksen her
   * finnes for det andre tilfellet — drift har opprettet en lenke med
   * administrator-token og gitt deg bare koden. Da settes adressen sammen her, så
   * ingen limer den sammen for hånd og deler ut en lenke som ikke virker.
   */
  function lenkekort(): HTMLElement {
    const kort = div('kort');
    const hode = div('kort-hode');
    hode.append(ikon('lenke'), h(2, 'kort-tittel', 'Bygg registreringslenke av en kode'));
    kort.append(hode);
    if (ctx.meg.person.kanAttestere) {
      kort.append(p('kort-brød', 'Du har attestasjonsrett og kan lage invitasjonslenker selv — '
        + 'gjør det på «Inviter». Der ser du også hvilke lenker som er utstedt, og kan '
        + 'tilbakekalle dem.'));
      kort.append(knapp('Gå til Inviter', () => ctx.gåTil('inviter'),
        { variant: 'stille', ikon: ikon('inviter') }));
    }
    kort.append(p('kort-brød', 'Har du i stedet fått en løs kode fra drift (fra '
      + '`POST /lenker` med administrator-token), lim den inn her for å få hele adressen.'));

    const skjema = document.createElement('form');
    skjema.className = 'skjema-rad';
    skjema.noValidate = true;
    const kodefelt = felt('Kode fra POST /lenker', { plassholder: 'lim inn koden' });
    const lag = knapp('Bygg lenke', undefined, { variant: 'primær', type: 'submit' });
    skjema.append(kodefelt.rot, lag);
    kort.append(skjema);

    const utplass = div('');
    kort.append(utplass);

    skjema.addEventListener('submit', (e) => {
      e.preventDefault();
      const k = kodefelt.input.value.trim();
      if (!k) {
        kodefelt.settFeil('Lim inn koden du fikk fra tjenesten.');
        return kodefelt.input.focus();
      }
      kodefelt.settFeil(null);
      const url = registreringsLenke(globalThis.location.origin, k);
      const rad = div('hemmelighet');
      rad.append(span('hemmelighet-etikett', 'Registreringslenke'));
      const kode = document.createElement('code');
      kode.className = 'kodeverdi';
      kode.textContent = url;
      kode.tabIndex = 0;
      rad.append(kode);
      rad.append(kopierknapp(url, 'Registreringslenken', ctx.kvitter));
      rad.append(p('felt-hjelp', 'Lenken er en hemmelighet: den gir rett til å opprette en '
        + 'konto som kan føre timer. Del den i en lukket kanal, og bare med én person '
        + 'hvis den er engangs.'));
      sett(utplass, rad);
    });

    return kort;
  }
}
